import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Badge as PolarisBadge,
  Card,
  TextField,
  Button as PolarisButton,
} from "@shopify/polaris";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import PaginationControls from "components/pagination/PaginationControls";
import { PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import withAreaQuery from "features/work-security-areas/with-area-query";
import {
  fetchWorkSecurityRoles,
  setWorkSecurityRoleArchived,
  WorkSecurityListPagination,
  WorkSecurityRole,
} from "services/work-security.service";
import { WorkSecurityArea, WorkSecurityAreaScopeProps } from "services/work-security-areas.service";

interface Props extends WorkSecurityAreaScopeProps {
  /** Accessible areas (from the module header's useAreaScope) — used to label the "Sritis" column. */
  areas?: WorkSecurityArea[];
}

/** Falls back to a generic label for a role whose area was since archived (excluded from `areas`). */
function areaLabel(areas: WorkSecurityArea[], t: any, areaId?: string | null): string {
  const found = areaId ? areas.find((a) => a.id === areaId) : undefined;
  return found ? found.name : t("area_unknown", { defaultValue: "Other sphere" });
}

// `areaId`/`selectedArea` follow the PR-2 §P1 hard rule (see useAreaScope):
// empty when there is nothing to disambiguate, so the fetch call below is
// unaffected for the common single-area company.
export default function WorkSecurityArchivedPanel({
  areaId,
  selectedArea,
  areas = [],
}: Props = {}) {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  // "All areas" mode, and only worth a column when there is more than one
  // sphere to disambiguate — matches the module header's own switcher gate.
  const showAreaColumn = !areaId && areas.length > 1;

  const [roles, setRoles] = useState<WorkSecurityRole[]>([]);
  const [pagination, setPagination] =
    useState<WorkSecurityListPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });
  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextSearch] = useState("");
  const rolesRequestRef = useRef(0);

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];
  const hasActiveFilters = Boolean(debouncedTextSearch.trim());

  // Switching spheres resets pagination the same way switching companies
  // does (see work-security.tsx's own roles-tab reset effect, PR-2 §P1).
  useEffect(() => {
    setPaginationState({ page: 1, pageSize: 20 });
  }, [areaId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTextSearch((current) => {
        if (current === textSearch) return current;
        setPaginationState((prev) => ({ ...prev, page: 1 }));
        return textSearch;
      });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [textSearch]);

  const loadRoles = useCallback(async () => {
    const requestId = ++rolesRequestRef.current;
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchWorkSecurityRoles(companyId, {
        page,
        pageSize,
        textSearch: debouncedTextSearch,
        archived: "only",
        ...(areaId ? { areaId } : {}),
      });
      if (requestId !== rolesRequestRef.current) return;
      setLoadError(false);
      setRoles(data.entries || []);
      setPagination(data.pagination || null);
    } catch (err) {
      if (requestId !== rolesRequestRef.current) return;
      console.error("Failed to load archived roles", err);
      setLoadError(true);
    } finally {
      if (requestId === rolesRequestRef.current) setLoading(false);
    }
  }, [companyId, page, pageSize, debouncedTextSearch, areaId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleRestore = async (roleId: string) => {
    if (!companyId) return;
    try {
      setActionError(null);
      setRestoringId(roleId);
      await setWorkSecurityRoleArchived(companyId, roleId, false);
      await loadRoles();
    } catch (err) {
      console.error("Failed to restore role", err);
      setActionError(
        t("failed_restore_role", {
          defaultValue: "Unable to restore role. Please try again.",
        }),
      );
    } finally {
      setRestoringId(null);
    }
  };

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : roles.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
        ? Math.ceil(totalItems / pageSize)
        : 0;
  const hasNextPage = totalPages > 0 ? page < totalPages : false;
  const hasPreviousPage = page > 1;
  const shouldShowPagination = Boolean(pagination && totalItems > 0);
  const showEmptyArchive =
    !loading && !loadError && !hasActiveFilters && roles.length === 0;
  const showEmptyFilter =
    !loading && !loadError && hasActiveFilters && roles.length === 0;

  return (
    <div className="space-y-4 mt-4">
      {actionError && (
        <Banner tone="critical" onDismiss={() => setActionError(null)}>
          {actionError}
        </Banner>
      )}

      <Card>
        <div className="p-2">
          <TextField
            label={t("search_roles", { defaultValue: "Search Roles" })}
            value={textSearch}
            onChange={setTextSearch}
            placeholder={t("search_roles_placeholder", {
              defaultValue: "Search by role name or description...",
            })}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setTextSearch("")}
          />
        </div>
      </Card>

      <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
        {loading && roles.length === 0 ? (
          <DataTableSkeleton rows={8} columns={5} />
        ) : loadError && roles.length === 0 ? (
          <div className="text-center py-16 px-4">
            <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
              {t("roles_load_failed_title", {
                defaultValue: "Unable to Load Roles",
              })}
            </h3>
            <div className="mt-6">
              <button
                type="button"
                onClick={loadRoles}
                className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 cursor-pointer"
              >
                {t("retry", { defaultValue: "Retry" })}
              </button>
            </div>
          </div>
        ) : showEmptyFilter ? (
          <div className="text-center py-16 px-4">
            <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
              {t("no_roles_match_filters", {
                defaultValue: "No roles match your filters",
              })}
            </h3>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {t("no_roles_match_filters_description", {
                defaultValue: "Try adjusting your search or filter criteria.",
              })}
            </p>
          </div>
        ) : showEmptyArchive ? (
          <div className="text-center py-16 px-4">
            <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
              {t("no_archived_roles", { defaultValue: "No archived roles" })}
            </h3>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {t("no_archived_roles_description", {
                defaultValue:
                  "Roles you archive will appear here and can be restored.",
              })}
            </p>
          </div>
        ) : (
          <>
            <DataTable
              collection={roles}
              rowUrl={(item: any) => withAreaQuery(`/app/${companyId}/work-security/${item.id}`, areaId)}
              columns={[
                { key: "#" },
                {
                  key: "name",
                  name: t("role_name", { defaultValue: "Role Name" }),
                  mobileView: "topLeft",
                },
                {
                  key: "description",
                  name: t("description", { defaultValue: "Description" }),
                  type: "custom",
                  mobileView: "bottomLeft",
                  render: (item: any) =>
                    item.description || (
                      <span className="text-zinc-400 italic">
                        {t("no_description", {
                          defaultValue: "No description",
                        })}
                      </span>
                    ),
                },
                ...(showAreaColumn
                  ? [
                      {
                        key: "area",
                        name: t("area_switcher_label", { defaultValue: "Sritis" }),
                        type: "custom" as const,
                        render: (item: WorkSecurityRole) => (
                          <PolarisBadge>{areaLabel(areas, t, item.area)}</PolarisBadge>
                        ),
                      },
                    ]
                  : []),
                {
                  key: "archivedAt",
                  name: t("archived_at", { defaultValue: "Archived At" }),
                  type: "date",
                  mobileView: "topRight",
                },
                {
                  key: "actions",
                  name: "",
                  type: "custom",
                  mobileView: "bottomRight",
                  render: (item: any) => (
                    // The row opens the role, so the button must not let its tap through.
                    <div className="text-right" onClick={(e) => e.stopPropagation()}>
                      <PolarisButton
                        variant="plain"
                        loading={restoringId === item.id}
                        onClick={() => handleRestore(item.id)}
                      >
                        {t("restore", { defaultValue: "Restore" })}
                      </PolarisButton>
                    </div>
                  ),
                },
              ]}
            />
            {shouldShowPagination && (
              <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={totalItems}
                  totalPages={totalPages}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onPageChange={(nextPage: number) =>
                    nextPage > 0 &&
                    nextPage !== page &&
                    setPaginationState((prev) => ({ ...prev, page: nextPage }))
                  }
                  onPageSizeChange={(nextPageSize: number) =>
                    nextPageSize > 0 &&
                    nextPageSize !== pageSize &&
                    setPaginationState({ page: 1, pageSize: nextPageSize })
                  }
                  pageSizeOptions={pageSizeOptions}
                  isLoading={loading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
